import { z } from 'zod';
import { calculationBasisSchema } from './financials.js';
import { conditionBandSchema } from './asset.js';
import { listingCarbonClaimFieldsSchema } from './carbon.js';
import { offerStatusSchema, offerKindSchema } from './offer.js';
import {
  pricingSchema,
  evidenceSchema,
  lotMemberForBuyerSchema,
  listingCommercialTermsSchema,
  listingOwnerTermsSchema,
  listingTechnicalDetailsSchema,
} from './buyer-tier.js';

/**
 * Marketplace listing DTOs (RUF-308).
 *
 * Pricing is MEDIAN-ONLY — a listing never carries FMV low/high/confidence. The
 * asking price is the seller's ask; it defaults (server-side) to the asset's
 * latest FMV median but is a seller-editable number.
 *
 * This slice only creates 'asset' listings. The 'sku' listing_type + quantity are
 * modelled now (RUF-309) but not yet creatable.
 */

export const listingTypeSchema = z.enum(['asset', 'sku', 'lot']);
export type ListingType = z.infer<typeof listingTypeSchema>;

/**
 * Kind of exchange lot (asset_lot.lot_type): 'cluster' = grouped servers,
 * 'lot' = generic grouping (e.g. GPU cards). Named assetLotType* to avoid the
 * unrelated market-comp lotType ('individual' | 'lot') in dto/sku.ts.
 */
export const assetLotTypeSchema = z.enum(['cluster', 'lot']);
export type AssetLotType = z.infer<typeof assetLotTypeSchema>;

/**
 * SIL-643: sale mechanic — INDEPENDENT axis from listingType (the sale
 * target: asset|sku|lot). buy_now is the default/existing behavior; auction
 * listings carry an `auction` terms object (see listingAuctionTermsSchema).
 * Private/sealed offers are auctions with bidVisibility='sealed', not a
 * third mechanic — see plans/exchange-mechanics-and-export-control.md §3.
 */
export const saleMechanicSchema = z.enum(['buy_now', 'auction']);
export type SaleMechanic = z.infer<typeof saleMechanicSchema>;

/**
 * 'open' = current high bid + the caller's own position visible to bidders,
 * anonymized (mirrors the 734/739 marketplace title-anonymization
 * convention). 'sealed' = a private/sealed auction — the seller sees bids
 * only at close and cannot counter mid-auction. Bidding itself is SIL-647.
 */
export const bidVisibilitySchema = z.enum(['open', 'sealed']);
export type BidVisibility = z.infer<typeof bidVisibilitySchema>;

/**
 * Auction terms supplied by the seller at listing create time (SIL-643).
 * reservePriceUsd is INPUT-ONLY — it is never echoed back in any response
 * DTO (see `listingAuctionTermsSchema` below, which omits it entirely).
 * closesAt must be a future ISO datetime; validated server-side.
 */
export const createAuctionTermsInputSchema = z.object({
  reservePriceUsd: z.number().positive(),
  minIncrementUsd: z.number().positive(),
  closesAt: z.string().datetime({ offset: true }),
  bidVisibility: bidVisibilitySchema,
});
export type CreateAuctionTermsInput = z.infer<typeof createAuctionTermsInputSchema>;

/**
 * Buyer- and seller-safe auction terms as embedded on a listing response.
 * reservePriceUsd is DELIBERATELY ABSENT — never exposed to buyers (the
 * point of this slice), and never exposed to the seller either for this
 * slice (759_listing_auction.sql's column-level GRANT enforces the same
 * omission at the DB layer, independent of this DTO).
 */
export const listingAuctionTermsSchema = z.object({
  minIncrementUsd: z.number(),
  /** ISO datetime — the scheduled close. Drives the marketplace countdown. */
  closesAt: z.string(),
  bidVisibility: bidVisibilitySchema,
});
export type ListingAuctionTerms = z.infer<typeof listingAuctionTermsSchema>;

/**
 * SIL-647/653: current bid status on an auction listing, sourced server-side
 * via client_listing_auction_bid_status() (765_bid.sql's SECURITY DEFINER
 * accessor, sealed behavior added by 768_sealed_bid_auctions.sql) — never a
 * raw `bid` table read (no client_readonly GRANT exists on that table at
 * all). Bidders are anonymized to each other: this NEVER carries any
 * bidder's identity, only the aggregate high bid/bid count + the caller's
 * own position.
 *
 * `highBidUsd`/`bidCount`/`minNextBidUsd`/`isLeading` are null for a
 * bid_visibility='sealed' listing until `closedAt` is set — ONLY
 * `yourHighBidUsd` is populated pre-close, for EVERY caller including the
 * owning seller (who has no bids of its own, so this is nothing at all).
 * Once `closedAt` is set (auction closed via close_due_auctions()), a
 * sealed listing reveals the aggregate exactly like an 'open' one.
 * `minNextBidUsd` stays null for a sealed listing even after close — sealed
 * bids have no minimum-increment rule to advertise (SIL-653) and a closed
 * auction accepts no further bids regardless.
 */
export const listingAuctionBidStatusSchema = z.object({
  bidVisibility: bidVisibilitySchema,
  highBidUsd: z.number().nullable(),
  bidCount: z.number().int().nullable(),
  yourHighBidUsd: z.number().nullable(),
  isLeading: z.boolean().nullable(),
  minNextBidUsd: z.number().nullable(),
  closesAt: z.string(),
  closedAt: z.string().nullable(),
});
export type ListingAuctionBidStatus = z.infer<typeof listingAuctionBidStatusSchema>;

/**
 * Body for POST /api/client/listings/:id/bids — place a bid on an open
 * auction listing (SIL-647).
 */
export const createBidInputSchema = z.object({
  amountUsd: z.number().positive(),
});
export type CreateBidInput = z.infer<typeof createBidInputSchema>;

/**
 * Response for POST /api/client/listings/:id/bids — mirrors place_bid()'s
 * jsonb return (765_bid.sql). `extended` = true when this bid landed inside
 * the soft-close window and pushed `closesAt` out.
 */
export const bidPlacedResponseSchema = z.object({
  bidId: z.string().uuid(),
  amountUsd: z.number(),
  closesAt: z.string(),
  extended: z.boolean(),
});
export type BidPlacedResponse = z.infer<typeof bidPlacedResponseSchema>;

export const listingStatusSchema = z.enum([
  'draft',
  'active',
  'under_offer',
  /** 669: committed forward sale — deposit held; ownership transfers at availableOn (daily sweep). */
  'pending_settlement',
  'sold',
  /** 669: expiresAt elapsed with no committed deal (fires from active only). */
  'expired',
  'withdrawn',
]);
export type ListingStatus = z.infer<typeof listingStatusSchema>;

/** ISO calendar date (yyyy-mm-dd) — forward-availability date fields. */
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected yyyy-mm-dd');

/**
 * A listing as returned to a client (seller manage view + buyer marketplace view).
 */
export const listingSchema = z.object({
  id: z.string().uuid(),
  customerOrgId: z.string().uuid(),
  listingType: listingTypeSchema,
  assetId: z.string().uuid().nullable(),
  skuId: z.string().uuid().nullable(),
  lotId: z.string().uuid().nullable(),
  quantity: z.number().int().nullable(),
  askingPriceUsd: z.number(),
  currencyCode: z.string(),
  status: listingStatusSchema,
  /** SIL-643: buy_now (default) or auction. */
  saleMechanic: saleMechanicSchema,
  /** SIL-643: non-null only for saleMechanic='auction'. Never carries reservePriceUsd. */
  auction: listingAuctionTermsSchema.nullable(),
  /**
   * SIL-643: OWNER-ONLY. This endpoint (GET/POST /api/client/listings) only
   * ever returns the CALLER's own listings, so it's safe to always populate
   * this for an auction listing — never present for a listing another org
   * could see (that's marketplaceListingSchema, dto/marketplace.ts, which
   * has no reservePriceUsd field at all). Null for buy_now listings. Sourced
   * server-side via client_listing_auction_reserve_price() (759's SECURITY
   * DEFINER accessor) — never the raw column, which stays column-grant-denied.
   */
  reservePriceUsd: z.number().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  displayTitle: z.string().nullable(),
  make: z.string().nullable(),
  /** 669: forward availability — NULL = available now (spot). */
  availableOn: isoDateSchema.nullable(),
  /** 669: optional expiry for an active listing (ISO timestamp). */
  expiresAt: z.string().nullable(),
  /**
   * SIL-300 (W3, spec slice 7) — which `financial_scenario` (if any) modeled
   * this listing. Null for listings created outside the scenario bridge.
   * Frozen post-draft (698's `tg_listing_immutable_cols` arm).
   */
  sourceScenarioId: z.string().uuid().nullable(),
  /**
   * SIL-300 (W3) — the full `CalculationBasis` snapshot (unit price,
   * formulaId, fmv band, asOf) computed server-side from the source
   * scenario's cohort at creation time. Null when `sourceScenarioId` is
   * null. Written once, never recomputed (698's freeze arm).
   */
  modeledBasis: calculationBasisSchema.nullable(),
});
export type Listing = z.infer<typeof listingSchema>;

export const listingListResponseSchema = z.object({
  data: z.array(listingSchema),
});
export type ListingListResponse = z.infer<typeof listingListResponseSchema>;

/**
 * Body for POST /api/client/listings — create an ASSET listing (RUF-308).
 *
 * askingPriceUsd is optional: when omitted the server falls back to the asset's
 * latest FMV median. When supplied it must be a positive number (the seller's
 * override). listingType is optional here (defaults to 'asset') so the existing
 * asset list-for-sale flow keeps working unchanged.
 */
export const createAssetListingInputSchema = z.object({
  listingType: z.literal('asset').optional(),
  assetId: z.string().uuid(),
  /**
   * SIL-2409 (O3a): ask the server to make the asset's public passport public as part of creating this listing. Honoured by the
   * single-asset create path ONLY, and only takes effect for an org ADMIN (the database re-checks): a member sending `true` still
   * gets the listing, with `passportPublished: false` in the response. Absent or false never changes publication.
   */
  publishPassport: z.boolean().optional(),
  /**
   * SIL-1648: units offered. An asset listing on a multi-unit line must offer
   * the WHOLE line (quantity === asset.quantity), enforced by the database;
   * a mismatch is a 409. Omitted = 1 unit (a serialized asset). The asking
   * price stays per unit.
   */
  quantity: z.number().int().positive().optional(),
  /**
   * SIL-300 (W3, spec slice 7): IGNORED (never read) when `sourceScenarioId`
   * is supplied — the modeled price is recomputed server-side from the
   * scenario cohort, never accepted from the client (a client-posted price
   * could otherwise launder an arbitrary number into "modeled").
   */
  askingPriceUsd: z.number().positive().optional(),
  /**
   * 669: forward availability — the date the asset frees up and the sale
   * settles. REQUIRED (and must be future) when the asset is in service;
   * optional otherwise. Capped server-side at 12 months from creation.
   * Asset listings only — deliberately absent from the sku/lot input schemas.
   */
  availableOn: isoDateSchema.optional(),
  /** 669: optional expiry (ISO timestamp, must be in the future at set time). */
  expiresAt: z.string().datetime({ offset: true }).optional(),
  /**
   * SIL-300 (W3) scenario-to-listing bridge (spec slice 7): the saved
   * `financial_scenario` this listing was modeled from. Requires
   * `sourceCohortId` (must both be provided together, or neither). Resolves
   * under the caller's GUC — a foreign-org id resolves to zero rows, same
   * uniform 404 as a not-found scenario.
   */
  sourceScenarioId: z.string().uuid().optional(),
  /** SIL-300 (W3) — the specific cohort of `sourceScenarioId` whose class
   * aggregate prices this listing. Required together with `sourceScenarioId`. */
  sourceCohortId: z.string().uuid().optional(),
  /**
   * SIL-643: sale mechanic. Omitted/'buy_now' preserves today's behavior
   * exactly (askingPriceUsd flow, unchanged). 'auction' REQUIRES `auction`
   * (below) and IGNORES askingPriceUsd (an auction has no fixed ask — the
   * server derives a display price server-side if/when needed; for now the
   * asking-price field is simply not used on the auction path).
   */
  saleMechanic: saleMechanicSchema.optional(),
  /** Required together with saleMechanic='auction'; absent/ignored otherwise. */
  auction: createAuctionTermsInputSchema.optional(),
}).refine((v) => (v.sourceScenarioId == null) === (v.sourceCohortId == null), {
  message: 'sourceScenarioId and sourceCohortId must both be supplied together',
  path: ['sourceCohortId'],
}).refine((v) => (v.saleMechanic === 'auction') === (v.auction != null), {
  message: '`auction` terms are required when saleMechanic is auction, and only then',
  path: ['auction'],
});
export type CreateAssetListingInput = z.infer<typeof createAssetListingInputSchema>;

/**
 * Body for POST /api/client/listings — create a fungible SKU listing (RUF-309):
 * a quantity of a sku. askingPriceUsd is optional and defaults (server-side) to
 * the sku's market-comp median; when supplied it must be positive. quantity must
 * be a positive integer and is gated against owned, listable inventory.
 */
export const createSkuListingInputSchema = z.object({
  listingType: z.literal('sku'),
  skuId: z.string().uuid(),
  quantity: z.number().int().positive(),
  askingPriceUsd: z.number().positive().optional(),
  /** SIL-2409: only a single-asset listing can publish a passport; the other create paths reject the field. */
  publishPassport: z.never().optional(),
});
export type CreateSkuListingInput = z.infer<typeof createSkuListingInputSchema>;

/**
 * SIL-2062: ceiling of listing.asking_price_usd (numeric(12,2)). A per-unit price
 * and the lot total it adds up to must both fit.
 */
export const LOT_UNIT_PRICE_MAX_USD = 9_999_999_999.99;

/** True when `n` has at most two decimal places (cents). */
function hasAtMostCents(n: number): boolean {
  return Math.abs(n * 100 - Math.round(n * 100)) < 1e-6;
}

/**
 * SIL-2062: the seller's asking price per unit of ONE SKU group in a lot.
 * `unitPriceUsd` is a plain number > 0 with at most two decimals; the database
 * (client_set_lot_unit_prices) re-validates and derives the lot total.
 */
export const lotUnitPriceInputSchema = z.object({
  skuId: z.string().uuid(),
  unitPriceUsd: z
    .number()
    .positive()
    .max(LOT_UNIT_PRICE_MAX_USD)
    .refine(hasAtMostCents, { message: 'at most 2 decimal places' }),
});
export type LotUnitPriceInput = z.infer<typeof lotUnitPriceInputSchema>;

/** 1..500 per-SKU prices, one per SKU. Coverage of the lot's SKUs is checked server-side. */
export const lotUnitPricesInputSchema = z
  .array(lotUnitPriceInputSchema)
  .min(1)
  .max(500)
  .refine((rows) => new Set(rows.map((r) => r.skuId)).size === rows.length, {
    message: 'duplicate skuId',
  });

/**
 * Body for POST /api/client/listings — list a whole asset_lot (RUF-376).
 * askingPriceUsd defaults (server-side) to the lot's aggregate FMV median (sum of
 * member medians); when supplied it must be positive.
 *
 * SIL-2062: `unitPrices` prices the lot per SKU group instead; the lot total is
 * then DERIVED (sum of unit price x units), so it is mutually exclusive with
 * `askingPriceUsd`.
 */
export const createLotListingInputSchema = z
  .object({
    listingType: z.literal('lot'),
    lotId: z.string().uuid(),
    askingPriceUsd: z.number().positive().optional(),
    unitPrices: lotUnitPricesInputSchema.optional(),
    /** SIL-2409: only a single-asset listing can publish a passport; the other create paths reject the field. */
    publishPassport: z.never().optional(),
  })
  .refine((b) => b.unitPrices === undefined || b.askingPriceUsd === undefined, {
    message: 'unitPrices and askingPriceUsd are mutually exclusive',
    path: ['unitPrices'],
  });
export type CreateLotListingInput = z.infer<typeof createLotListingInputSchema>;

/**
 * POST /api/client/listings body — asset XOR sku XOR lot listing. The sku + lot
 * branches are discriminated by listingType; anything else parses as an asset
 * listing.
 */
export const createListingInputSchema = z.union([
  createSkuListingInputSchema,
  createLotListingInputSchema,
  createAssetListingInputSchema,
]);
export type CreateListingInput = z.infer<typeof createListingInputSchema>;

/**
 * GET /api/client/listings/sku-options — a sku the seller has listable inventory
 * for, with the owned eligible quantity and the market-comp median to prefill the
 * asking price. Median-only — no FMV low/high/confidence.
 */
export const skuListingOptionSchema = z.object({
  skuId: z.string().uuid(),
  make: z.string().nullable(),
  model: z.string().nullable(),
  shortDescription: z.string().nullable(),
  eligibleQuantity: z.number().int(),
  marketCompPriceUsd: z.number().nullable(),
});
export type SkuListingOption = z.infer<typeof skuListingOptionSchema>;

export const skuListingOptionsResponseSchema = z.object({
  data: z.array(skuListingOptionSchema),
});
export type SkuListingOptionsResponse = z.infer<typeof skuListingOptionsResponseSchema>;

/**
 * An offer as embedded in GET /api/client/listings/:id's `offers` array
 * (SIL-322-adjacent) — a THINNER shape than the standalone `offerSchema`
 * (dto/offer.ts): no isOwnOffer/counterAmountUsd/refusalReason/
 * listingAvailableOn/displayTitle/conditionBand/listingViewable, none of
 * which the listing-detail route currently selects or returns. Do not widen
 * this to match offerSchema without also changing the route — this schema is
 * a faithful mirror of today's wire shape, not an aspiration.
 */
export const listingDetailOfferSchema = z.object({
  id: z.string().uuid(),
  listingId: z.string().uuid(),
  // SIL-2276: no buyerCustomerOrgId - seller-visible, stable across deals.
  submittedBy: z.string().uuid().nullable(),
  amountUsd: z.number(),
  currencyCode: z.string(),
  status: offerStatusSchema,
  /**
   * SIL-1427: 'offer' (default) | 'buy_now' — the listing-detail owner offers
   * table labels a buy_now row "Buy now request" and never offers Counter on
   * it (migration 972's tg_offer_status_transition already rejects that
   * server-side; this is the UI mirroring it, not the boundary).
   */
  kind: offerKindSchema,
  /** SIL-1427: non-null only for kind='buy_now' — the seller's 48h SLA. */
  expiresAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ListingDetailOffer = z.infer<typeof listingDetailOfferSchema>;

/**
 * SIL-1331 (review #10): grouped, tier-independent summary of a lot's
 * contents, embedded in `listingDetailSchema.members` (lot listings only) —
 * sourced from `listing_lot_contents_public()`. No ids, serials,
 * fingerprints or health: a raw member asset row is verified-buyer-only
 * (955's marketplace-visibility policies), so this is the safe summary every
 * tier sees. Per-node detail (fingerprint, chassis serial, verdict, health)
 * moved to `listingDetailSchema.lotMembers` (verified buyers / owner only).
 */
export const listingLotMemberSchema = z.object({
  assetClassName: z.string().nullable(),
  conditionBand: conditionBandSchema.nullable(),
  displayTitle: z.string().nullable(),
  nodeCount: z.number().int(),
});
export type ListingLotMember = z.infer<typeof listingLotMemberSchema>;

/**
 * SIL-2062: one SKU group of a per-unit priced lot, in the response of the edit
 * route and in `listingDetailSchema.unitPrices`. `unitCount` is the member units
 * of that SKU in the lot; line total = unitPriceUsd x unitCount.
 */
export const lotUnitPriceRowSchema = z.object({
  skuId: z.string().uuid(),
  /** Buyer-safe SKU label (sku display name / model name); null if unreadable. */
  skuLabel: z.string().nullable(),
  unitPriceUsd: z.number(),
  unitCount: z.number().int(),
});
export type LotUnitPriceRow = z.infer<typeof lotUnitPriceRowSchema>;

/** PUT /api/client/listings/:id/unit-prices body (SIL-2062). */
export const setLotUnitPricesInputSchema = z.object({
  unitPrices: lotUnitPricesInputSchema,
});
export type SetLotUnitPricesInput = z.infer<typeof setLotUnitPricesInputSchema>;

/** PUT /api/client/listings/:id/unit-prices response (SIL-2062): the new derived total + rows. */
export const setLotUnitPricesResultSchema = z.object({
  listingId: z.string().uuid(),
  askingPriceUsd: z.number(),
  unitPrices: z.array(lotUnitPriceRowSchema),
});
export type SetLotUnitPricesResult = z.infer<typeof setLotUnitPricesResultSchema>;

/** SIL-190 — lot-level embodied-carbon total embedded in
 * `listingDetailSchema.lotEnvironmental` (lot listings only): per-member
 * latest snapshots summed server-side (values and min/max bounds summed
 * independently). */
export const listingLotEnvironmentalSchema = z.object({
  totalEmbodiedKgco2eq: z.number(),
  totalMin: z.number(),
  totalMax: z.number(),
  methodologyVersion: z.string().nullable(),
  computedAssetCount: z.number().int(),
  pendingClassificationCount: z.number().int(),
  aggregationLabel: z.string(),
  disclaimer: z.string(),
});
export type ListingLotEnvironmental = z.infer<typeof listingLotEnvironmentalSchema>;

/**
 * GET /api/client/listings/:id — full listing detail view (seller manage +
 * buyer marketplace detail). A DISTINCT, WIDER shape than `listingSchema`
 * (the create/list DTO) — it carries joined display fields (manufacturer/
 * asset-class/SKU names), owner-only fields (customerOrgId, assetSerialNumber
 * — null unless `isOwner`), the SIL-190/SIL-191 embedded lot-carbon and
 * avoided-carbon scalars, and the visible `offers` array. Modelled FLAT,
 * faithful to today's wire shape — NOT a discriminated union on listingType
 * (deferred in review); `members`/`lotEnvironmental` are simply null for
 * non-lot listings and `assetId`/`skuId`/`lotId`/`quantity` are null per the
 * usual "only one populated" convention.
 *
 * SIL-322: a customer-app local mirror of this shape had narrowed
 * `offers[].status` to a 4-state literal (missing countered/deposit_held/
 * completed/refused) even though the route has always returned the full
 * 8-state `offerStatusSchema` — this shared schema is now the single
 * enforced source of truth for both producer and consumer.
 */
export const listingDetailSchema = z.object({
  id: z.string().uuid(),
  /** Server-computed ownership (row.customerOrgId === caller's customer_org).
   * Drives seller-vs-buyer view; do NOT re-derive from orgId, which is the
   * seller/buyer *profile* org and differs from the owning customer_org. */
  isOwner: z.boolean(),
  /** Owner-only — null unless `isOwner` (never leak seller identity to a browser). */
  customerOrgId: z.string().uuid().nullable(),
  listingType: listingTypeSchema,
  /** Present for listingType 'asset'; the embedded digital passport (RUF-555)
   * renders from it — buyers on an active/under_offer listing can view it
   * (server-enforced via migration 606 marketplace-visibility RLS). */
  assetId: z.string().uuid().nullable(),
  /** Public passport handle `<prefix>-<token>` of `assetId` (formatFingerprint); null when `assetId` is. Keys the passport URL. */
  assetFingerprint: z.string().nullable(),
  /** Present for listingType 'lot'. */
  lotId: z.string().uuid().nullable(),
  /** Present (non-null) for listingType 'lot' — grouped lot contents (asset
   * class, condition band, display title, node count), sourced from
   * `listing_lot_contents_public()`. Every tier sees this (SIL-1331 review
   * #10); per-node detail is `lotMembers` below. */
  members: z.array(listingLotMemberSchema).nullable(),
  /**
   * SIL-1331: present only for listingType 'lot' AND a verified buyer (or
   * the owner) — per-node chassis serial, verdict, evidence tier and
   * custody, sourced from `listing_lot_members_for_buyer()`. Null for an
   * unverified non-owner viewing a lot listing, and null for non-lot
   * listings.
   */
  lotMembers: z.array(lotMemberForBuyerSchema).nullable(),
  /**
   * SIL-2062: per-SKU unit asking prices of a lot listing priced per unit, from
   * `client_listing_lot_unit_prices()`. Present only for the owner or a verified
   * buyer on a live, exact-price, not-offers-only listing; null otherwise (an
   * unverified or band/ask/offers-only viewer keeps the range / hidden price) and
   * for a flat-priced lot or any non-lot listing. Never in the public/grid shapes.
   */
  unitPrices: z.array(lotUnitPriceRowSchema).nullable().optional(),
  /** Present (non-null) for listingType 'lot' — lot embodied-carbon total (SIL-190). */
  lotEnvironmental: listingLotEnvironmentalSchema.nullable(),
  skuId: z.string().uuid().nullable(),
  quantity: z.number().int().nullable(),
  /**
   * SIL-1331: tier-tagged pricing (spec 5.2) — replaces the old flat
   * `askingPriceUsd`. An unverified buyer (non-owner) gets a bucketed range
   * only; a verified buyer or the owner gets the exact price and FMV band.
   */
  pricing: pricingSchema,
  /**
   * SIL-1331: tier-tagged evidence (spec 5.2) — an unverified buyer
   * (non-owner) gets booleans/region/node-count only; a verified buyer or
   * the owner gets the site, per-run counts and a custody summary.
   */
  evidence: evidenceSchema,
  currencyCode: z.string(),
  status: listingStatusSchema,
  /** SIL-643: buy_now (default) or auction. */
  saleMechanic: saleMechanicSchema,
  /** SIL-643: non-null only for saleMechanic='auction'. Never carries reservePriceUsd. */
  auction: listingAuctionTermsSchema.nullable(),
  /**
   * SIL-643: OWNER-ONLY — null unless `isOwner`, mirroring customerOrgId/
   * assetSerialNumber below (never leak the seller's reserve to a buyer on
   * this bidirectional detail route). Sourced via client_listing_auction_
   * reserve_price() (759's SECURITY DEFINER accessor), which already returns
   * NULL for a non-owner caller — the isOwner gate here is a documented
   * belt-and-suspenders, not the sole enforcement point.
   */
  reservePriceUsd: z.number().nullable(),
  /**
   * SIL-647: non-null only for saleMechanic='auction' — current high bid +
   * the caller's own position (never another bidder's identity). Null for a
   * buy_now listing, or for an auction listing not visible to the caller
   * (mirrors client_listing_auction_bid_status()'s NULL cases).
   */
  auctionBidStatus: listingAuctionBidStatusSchema.nullable(),
  /** 669: forward availability — null = available now (buyer-safe: part of
   * the offer terms). */
  availableOn: isoDateSchema.nullable(),
  displayTitle: z.string().nullable(),
  conditionBand: conditionBandSchema.nullable(),
  manufacturerId: z.string().uuid().nullable(),
  manufacturerName: z.string().nullable(),
  generation: z.string().nullable(),
  assetClassId: z.string().uuid().nullable(),
  assetClassName: z.string().nullable(),
  /** Owner-only — null unless `isOwner`. */
  assetSerialNumber: z.string().nullable(),
  skuModelName: z.string().nullable(),
  skuShortDescription: z.string().nullable(),
  /**
   * SIL-1345: `listing.created_at` as an explicit buyer-facing field. Equal to
   * `createdAt` today and kept as its own key on purpose — `createdAt`/
   * `updatedAt` are row-audit metadata every DTO in this file carries, while
   * `listedAt` is a product fact the marketplace card and the detail header both
   * render. A future "relist" that resets the listing clock changes `listedAt`
   * without rewriting row history.
   */
  listedAt: z.string(),
  /**
   * SIL-1345: whether the listing will take an offer right now. Mirrors the
   * ACTUAL gate in POST /api/client/offers (status active or under_offer), not a
   * second rule that could drift from it.
   */
  acceptingOffers: z.boolean(),
  /**
   * SIL-1345: buyer-safe commercial terms, BOTH tiers (spec 3.1). Null = the
   * seller did not state it; the UI hides the tile rather than showing
   * "unknown".
   */
  ...listingCommercialTermsSchema.shape,
  /**
   * SIL-1345: OWNER-ONLY. `minOfferUsd` is present ONLY when `isOwner` — the key
   * is OMITTED for every buyer of either tier, not sent as null (see
   * `listingOwnerTermsSchema`). Distinct from `reservePriceUsd` above, which
   * predates this discipline and uses null.
   */
  ...listingOwnerTermsSchema.shape,
  /**
   * SIL-1345: technical-detail tiles from the SKU's `class_attributes`, both
   * tiers. A missing attribute is an ABSENT key, not null — `{}` means the
   * section renders nothing.
   */
  technicalDetails: listingTechnicalDetailsSchema,
  /**
   * SIL-1550: the SKU's resolved catalogue photo (own, else its family
   * master's), same shape and same resolver as `MarketplaceListing.image`
   * (`client_listing_primary_images`). `url` is CDN base + object key. Null
   * when there is no image; optional so a UI tolerates an API one version
   * behind (SIL-1377 skew rule) - absent renders generic art.
   */
  image: z
    .object({
      url: z.string(),
      source: z.string().nullable(),
      license: z.string().nullable(),
      attribution: z.string().nullable(),
      sourceUrl: z.string().nullable(),
    })
    .nullable()
    .optional(),
  ...listingCarbonClaimFieldsSchema.shape,
  createdAt: z.string(),
  updatedAt: z.string(),
  offers: z.array(listingDetailOfferSchema),
});
export type ListingDetail = z.infer<typeof listingDetailSchema>;

/**
 * GET /api/listings (ops admin) — a row in the all-orgs listings table.
 * Denormalized with the seller's legal name and manufacturer make/lot-type
 * for display; distinct from `listingSchema` (the client create/list DTO),
 * which never carries seller identity.
 */
export const adminListingRowSchema = z.object({
  id: z.string().uuid(),
  sellerOrgName: z.string().nullable(),
  listingType: listingTypeSchema,
  lotType: assetLotTypeSchema.nullable(),
  displayTitle: z.string().nullable(),
  make: z.string().nullable(),
  /** SIL-1508: null for a house-org no_fmv draft (995/996) — no price set yet. */
  askingPriceUsd: z.number().nullable(),
  status: listingStatusSchema,
  /** 669: forward availability — null = spot listing. */
  availableOn: z.string().nullable(),
  createdAt: z.string(),
});
export type AdminListingRow = z.infer<typeof adminListingRowSchema>;

export const adminListingListResponseSchema = z.object({
  data: z.array(adminListingRowSchema),
  nextCursor: z.string().nullable(),
});
export type AdminListingListResponse = z.infer<typeof adminListingListResponseSchema>;
